import type { FormattedReaction } from '@adamdotai/design-system/src/components/InlineReactions/types'
import type {
    EditorState,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from 'lexical'
import { ElementNode } from 'lexical'
import type { InlineIndicatorsNodeHeadless } from '../../../../nodes/InlineIndicatorsNode/InlineIndicatorsNodeHeadless'
import { $createInlineIndicatorsNodeHeadless } from '../../../../nodes/InlineIndicatorsNode/InlineIndicatorsNodeHeadless'
import type {
    CommonContentItemPayload,
    InlineIndicatorsChangeEvent,
} from '../../../../types/content-items'
import type { AdamNoteNode } from '../AdamNoteNode/AdamNoteNode'

type NotePayload = CommonContentItemPayload

const DEFAULT_NOTE_PAYLOAD: NotePayload = {
    commentsCount: 0,
    linksCount: 0,
    tagsCount: 0,
    reactions: [],
}

type SerializedNoteContainerNode = Spread<
    {
        type: 'note-container'
        version: 1
        id: string
        payload: NotePayload
    },
    SerializedElementNode
>

class NoteContainerNodeHeadless extends ElementNode {
    __id: string

    __payload: NotePayload

    hasTextEditable(): boolean {
        return true
    }

    getTextEditableNode(): AdamNoteNode {
        return this.getFirstChildOrThrow()
    }

    getInlineIndicatorsNode(): InlineIndicatorsNodeHeadless {
        return this.getLastChildOrThrow()
    }

    constructor(
        id?: string | null,
        payload?: Partial<NotePayload>,
        key?: NodeKey,
    ) {
        super(key)
        this.__id = id ?? ''
        this.__payload = {
            ...DEFAULT_NOTE_PAYLOAD,
            ...payload,
        }
    }

    static getType(): string {
        return 'note-container'
    }

    getContentType(): 'note' {
        return 'note'
    }

    static clone(node: NoteContainerNodeHeadless): NoteContainerNodeHeadless {
        return $createNoteContainerNodeHeadless(
            node.getId(),
            node.getPayload(),
            node.getKey(),
        )
    }

    static importJSON(
        serializedNode: SerializedNoteContainerNode,
    ): NoteContainerNodeHeadless {
        return $createNoteContainerNodeHeadless(
            serializedNode.id,
            serializedNode.payload,
        )
    }

    exportJSON(): SerializedNoteContainerNode {
        return {
            ...super.exportJSON(),
            type: 'note-container',
            version: 1,
            id: this.__id,
            payload: this.__payload,
        }
    }

    hasChanged(
        nodeInPrevEditorState: NoteContainerNodeHeadless,
        editorState: EditorState,
        prevEditorState: EditorState,
    ): boolean {
        const textContent = editorState.read(() =>
            this.getTextEditableNode()?.getTextContent(),
        )
        const prevTextContent = prevEditorState.read(() =>
            this.getTextEditableNode()?.getTextContent(),
        )
        return textContent !== prevTextContent
    }

    hasContent(): boolean {
        return !!this.getTextEditableNode()?.getTextContentSize()
    }

    getTextContent(): string {
        try {
            return this.getTextEditableNode().getTextContent()
        } catch {
            return ''
        }
    }

    getPayload() {
        return this.__payload
    }

    updatePayload(payload: Partial<NotePayload>) {
        const self = this.getWritable()
        self.__payload = {
            ...self.__payload,
            ...payload,
        }
    }

    getId() {
        return this.__id
    }

    setId(id: string): void {
        const self = this.getWritable()
        self.__id = id

        const indicators = this.getInlineIndicatorsNode()
        indicators.setId(id)
    }

    getReactions() {
        return this.__payload.reactions
    }

    setReactions(reactions: Array<FormattedReaction>) {
        this.updatePayload({ reactions })
        const inlineIndicatorsNode = this.getInlineIndicatorsNode()
        inlineIndicatorsNode.updatePayload({ reactions })
    }

    onSaveToTemplate() {
        const self = this.getWritable()

        self.__id = ''
        this.updatePayload(DEFAULT_NOTE_PAYLOAD)
    }

    onInlineIndicatorsChange(event: InlineIndicatorsChangeEvent) {
        this.updatePayload(event)
    }

    onChildChange(childType: string, event: unknown) {
        if (childType === 'inline-indicators') {
            this.onInlineIndicatorsChange(event as InlineIndicatorsChangeEvent)
        }
    }
}

function $createNoteContainerNodeHeadless(
    id?: string | null,
    payload?: Partial<NotePayload>,
    key?: NodeKey,
): NoteContainerNodeHeadless {
    return new NoteContainerNodeHeadless(id, payload, key)
}

function $isNoteContainerNodeHeadless(
    node: LexicalNode | null | undefined,
): node is NoteContainerNodeHeadless {
    return node instanceof NoteContainerNodeHeadless
}

const $createNestedNoteNodeHeadless = (
    noteFactory: () => AdamNoteNode,
): NoteContainerNodeHeadless => {
    const note = noteFactory()
    const container = $createNoteContainerNodeHeadless(note.getId())
    container.append(
        note,
        $createInlineIndicatorsNodeHeadless({
            itemId: note.getId() ?? undefined,
            itemType: 'note',
        }),
    )
    return container
}

export {
    type NotePayload,
    type SerializedNoteContainerNode,
    $createNestedNoteNodeHeadless,
    $isNoteContainerNodeHeadless,
    $createNoteContainerNodeHeadless,
    NoteContainerNodeHeadless,
}
